import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

import { audioService } from '@/services/api/audioService';

interface UseWaveSurferAudioProps {
  audioPath: string | null | undefined;
  onTimeUpdate?: (time: number) => void;
}

export const useWaveSurferAudio = ({
  audioPath,
  onTimeUpdate,
}: UseWaveSurferAudioProps) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const waveSurferRef = useRef<WaveSurfer | null>(null);

  /* ----------------------------- Fetch Presigned URL ---------------------------- */
  const fetchAudioUrl = useCallback(async () => {
    if (audioUrl || !audioPath) return;
    setIsLoading(true);
    try {
      const url = await audioService.fetchPresignedUrl(audioPath);
      setAudioUrl(url);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  }, [audioUrl, audioPath]);

  /* ------------------------------ Autoload on path change ------------------------------ */
  useEffect(() => {
    if (audioPath && !audioUrl) {
      fetchAudioUrl().catch((error) => {
        console.error('Failed to fetch audio URL:', error);
      });
    }
  }, [audioPath, audioUrl, fetchAudioUrl]);

  /* ------------------------------- WaveSurfer handlers ------------------------------- */
  const handleWaveformReady = (ws: WaveSurfer) => {
    waveSurferRef.current = ws;

    ws.on('audioprocess', (time: number) => {
      if (onTimeUpdate) onTimeUpdate(time);
    });
    ws.on('seeking', (progress: number) => {
      const duration = ws.getDuration();
      if (duration && onTimeUpdate) onTimeUpdate(progress * duration);
    });
    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('finish', () => setIsPlaying(false));
  };

  /* -------------------------------- Play / Pause -------------------------------- */
  const togglePlayPause = () => {
    const ws = waveSurferRef.current;
    if (ws) {
      ws.playPause();
    }
  };

  /* ---------------------------------- Cleanup ---------------------------------- */
  useEffect(
    () => () => {
      waveSurferRef.current?.destroy();
    },
    [],
  );

  return {
    audioUrl,
    isLoading,
    isPlaying,
    handleWaveformReady,
    togglePlayPause,
  } as const;
};
